/**
 * Pure helpers for grouping feed list items by date or entity. Used by
 * `FeedNativeList` according to the user's `feedGroupBy` preference; hidden
 * entity sections are filtered via `feedHiddenGroups`.
 */
import { formatDimensionValue } from "@/lib/formatDimensionValue";

import { formatFeedPartyLabel } from "./feedViewerParty";
import type { FeedEvent, FeedEventType } from "@/types/feed";
import { isCommercialDigestPayload } from "./FeedCommercialDigestDetail";
import { isOperationalDigestPayload } from "./FeedOperationalDigestDetail";
import {
  resolveFeedPartyKey,
  type ResolvedFeedParty,
} from "./feedEntityAliases";
import { EVENT_TYPE_LABEL } from "./feedVisuals";
import { collapseCutoffEvents, type CutoffGroupedItem } from "./feedCutoffGroups";
import { feedEventDisplayIso, feedEventDisplayTimestamp } from "./feedEventTime";

export type FeedGroupBy =
  | "date"
  | "none"
  | "booking"
  | "customer"
  | "sales_person"
  | "operator"
  | "agent"
  | "carrier"
  | "lane"
  | "event_type";

export const DEFAULT_FEED_GROUP_BY: FeedGroupBy = "date";

export const FEED_GROUP_BY_OPTIONS: { value: FeedGroupBy; label: string }[] = [
  { value: "date", label: "Date" },
  { value: "none", label: "None" },
  { value: "booking", label: "Booking" },
  { value: "carrier", label: "Carrier" },
  { value: "agent", label: "Agent" },
  { value: "sales_person", label: "Sales person" },
  { value: "operator", label: "Operator" },
  { value: "customer", label: "Customer" },
  { value: "lane", label: "Lane" },
  { value: "event_type", label: "Event type" },
];

const UNSCOPED_KEY = "__unscoped__";

/** Canonical party map so alias spellings collapse into one entity section. */
export type FeedPartyAliasLookup = Map<string, ResolvedFeedParty>;

function isPartyEntityGroupBy(groupBy: FeedGroupBy): boolean {
  return (
    groupBy === "customer" ||
    groupBy === "sales_person" ||
    groupBy === "agent"
  );
}

function canonicalizePartyRawKey(
  groupBy: FeedGroupBy,
  rawKey: string,
  aliases?: FeedPartyAliasLookup | null,
): { rawKey: string; label?: string } {
  if (rawKey === UNSCOPED_KEY || !isPartyEntityGroupBy(groupBy)) {
    return { rawKey };
  }
  const resolved = resolveFeedPartyKey(rawKey, aliases);
  return { rawKey: resolved.canonical, label: resolved.label };
}

export interface FeedDisplaySection {
  key: string;
  label: string;
  groupBy: FeedGroupBy;
  /** Raw entity key when grouped by entity (for sensitive masking). */
  rawKey?: string;
  /** Whether the user can hide this section from the feed. */
  declutterable: boolean;
  items: CutoffGroupedItem[];
  eventCount: number;
}

/**
 * Newest-first comparator on the *display* timestamp (mail `received_at` when
 * present, else `created_at`).
 *
 * The backend paginates strictly by `created_at` (ingest time), but rows are
 * displayed and grouped by their display timestamp — which diverges for
 * mail-backed events (`graph_mail` / `new_booking_mail`) ingested long after
 * they were received. Sorting here guarantees date/entity sections and the rows
 * inside them stay correctly ordered no matter what order pages (or live SSE
 * pushes) arrive in.
 *
 * Ties return `0` so the (stable) sort preserves the incoming backend order —
 * which is already `created_at DESC, id DESC` — instead of reshuffling rows
 * that share a display timestamp.
 */
function compareByDisplayDesc(a: FeedEvent, b: FeedEvent): number {
  return feedEventDisplayTimestamp(b) - feedEventDisplayTimestamp(a);
}

function dayKey(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "unknown";
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function feedDayLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Earlier";
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return "Today";
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return "Yesterday";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });
}

function itemIso(item: CutoffGroupedItem): string {
  return item.kind === "cutoff_group"
    ? feedEventDisplayIso(item.events[0])
    : feedEventDisplayIso(item.event);
}

function eventCountInItem(item: CutoffGroupedItem): number {
  return item.kind === "cutoff_group" ? item.events.length : 1;
}

function representativeEvent(item: CutoffGroupedItem): FeedEvent {
  return item.kind === "cutoff_group" ? item.events[0] : item.event;
}

export function feedDisplaySectionKey(
  groupBy: FeedGroupBy,
  rawKey: string,
): string {
  return `${groupBy}:${rawKey}`;
}

/** Prefer canonical scope tokens from portfolio digest cards when grouping. */
function portfolioDigestGroupKey(
  groupBy: FeedGroupBy,
  event: FeedEvent,
): string | null {
  const payload = event.payload;
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  const isDigest =
    isCommercialDigestPayload(payload) || isOperationalDigestPayload(payload);
  const isScopedBatch =
    record.kind === "batch" &&
    typeof record.scope === "string" &&
    record.scope !== "org" &&
    typeof record.dimension_key === "string";
  if (!isDigest && !isScopedBatch) return null;

  const digestScope = (payload as { scope?: { dimension?: string; key?: string } })
    .scope;
  const dimension = isScopedBatch
    ? (record.scope as string)
    : digestScope?.dimension;
  const key = isScopedBatch
    ? (record.dimension_key as string)
    : digestScope?.key;
  if (!dimension || !key?.trim()) return null;
  if (groupBy === "sales_person" && dimension === "sales_person") {
    return key;
  }
  if (groupBy === "operator" && dimension === "opened_by") {
    return key;
  }
  if (groupBy === "agent" && dimension === "agent") {
    return key;
  }
  return null;
}

function entityRawKey(
  groupBy: FeedGroupBy,
  event: FeedEvent,
  aliases?: FeedPartyAliasLookup | null,
): string {
  return resolveEntityRawKey(groupBy, event, aliases).rawKey;
}

function resolveEntityRawKey(
  groupBy: FeedGroupBy,
  event: FeedEvent,
  aliases?: FeedPartyAliasLookup | null,
): { rawKey: string; label?: string } {
  const digestKey = portfolioDigestGroupKey(groupBy, event);
  if (digestKey) {
    return canonicalizePartyRawKey(groupBy, digestKey, aliases);
  }

  const scope = event.entity_scope ?? {};
  let raw: string;
  switch (groupBy) {
    case "booking":
      raw = event.reference_id ?? scope.booking_ids?.[0] ?? UNSCOPED_KEY;
      break;
    case "customer":
      raw = scope.consignee ?? UNSCOPED_KEY;
      break;
    case "sales_person":
      raw = scope.sales_person ?? UNSCOPED_KEY;
      break;
    case "operator":
      raw = scope.opened_by ?? UNSCOPED_KEY;
      break;
    case "agent":
      raw = scope.agent ?? UNSCOPED_KEY;
      break;
    case "carrier":
      raw = scope.carrier ?? UNSCOPED_KEY;
      break;
    case "lane":
      raw = scope.lane_key ?? UNSCOPED_KEY;
      break;
    case "event_type":
      raw = event.event_type;
      break;
    default:
      raw = UNSCOPED_KEY;
  }
  return canonicalizePartyRawKey(groupBy, raw, aliases);
}

const UNSCOPED_LABEL: Partial<Record<FeedGroupBy, string>> = {
  booking: "No booking",
  customer: "No customer",
  sales_person: "No sales person",
  operator: "No operator",
  agent: "No agent",
  carrier: "No carrier",
  lane: "No lane",
  event_type: "Other",
};

export function feedEntitySectionLabel(
  groupBy: FeedGroupBy,
  rawKey: string,
): string {
  if (rawKey === UNSCOPED_KEY) {
    return UNSCOPED_LABEL[groupBy] ?? "Other";
  }
  switch (groupBy) {
    case "booking":
      return rawKey;
    case "carrier":
      return rawKey.toUpperCase();
    case "customer":
      return formatDimensionValue("consignee", rawKey);
    case "sales_person":
      return formatFeedPartyLabel("sales_person", rawKey);
    case "operator":
      return formatFeedPartyLabel("opened_by", rawKey);
    case "agent":
      return formatFeedPartyLabel("agent", rawKey);
    case "lane":
      return rawKey.replace("-", " → ");
    case "event_type":
      return EVENT_TYPE_LABEL[rawKey as FeedEventType] ?? rawKey;
    default:
      return rawKey;
  }
}

export function isFeedEntityGroupBy(groupBy: FeedGroupBy): boolean {
  return groupBy !== "date" && groupBy !== "none";
}

export function isFeedGroupLabelSensitive(groupBy: FeedGroupBy): boolean {
  return (
    groupBy === "customer" ||
    groupBy === "sales_person" ||
    groupBy === "operator" ||
    groupBy === "agent"
  );
}

function itemSectionKey(
  groupBy: FeedGroupBy,
  item: CutoffGroupedItem,
  aliases?: FeedPartyAliasLookup | null,
): string {
  const event = representativeEvent(item);
  if (groupBy === "date") {
    return feedDisplaySectionKey("date", dayKey(itemIso(item)));
  }
  return feedDisplaySectionKey(groupBy, entityRawKey(groupBy, event, aliases));
}

function appendItem(
  sections: FeedDisplaySection[],
  byKey: Map<string, FeedDisplaySection>,
  section: FeedDisplaySection,
  item: CutoffGroupedItem,
) {
  let existing = byKey.get(section.key);
  if (!existing) {
    existing = { ...section, items: [], eventCount: 0 };
    byKey.set(section.key, existing);
    sections.push(existing);
  }
  existing.items.push(item);
  existing.eventCount += eventCountInItem(item);
}

export function buildFeedDisplaySections(
  events: FeedEvent[],
  groupBy: FeedGroupBy,
  hiddenGroups: readonly string[] = [],
  partyAliases?: FeedPartyAliasLookup | null,
): FeedDisplaySection[] {
  // Order by display timestamp (newest first) before collapsing/grouping so
  // sections and rows stay chronological even when the cursor fetches by a
  // different key (ingest `created_at`) or live pushes arrive out of band.
  // `collapseCutoffEvents` also relies on a newest-first input.
  const ordered = [...events].sort(compareByDisplayDesc);
  const items = collapseCutoffEvents(ordered);
  const hidden = new Set(hiddenGroups);

  if (groupBy === "none") {
    if (items.length === 0) return [];
    return [
      {
        key: "flat",
        label: "",
        groupBy: "none",
        declutterable: false,
        items,
        eventCount: items.reduce((n, item) => n + eventCountInItem(item), 0),
      },
    ];
  }

  const sections: FeedDisplaySection[] = [];
  const byKey = new Map<string, FeedDisplaySection>();

  for (const item of items) {
    const sectionKey = itemSectionKey(groupBy, item, partyAliases);
    if (hidden.has(sectionKey)) continue;

    if (groupBy === "date") {
      const iso = itemIso(item);
      appendItem(
        sections,
        byKey,
        {
          key: sectionKey,
          label: feedDayLabel(iso),
          groupBy: "date",
          declutterable: false,
          items: [],
          eventCount: 0,
        },
        item,
      );
      continue;
    }

    const resolved = resolveEntityRawKey(
      groupBy,
      representativeEvent(item),
      partyAliases,
    );
    const label = resolved.label
      ? feedEntitySectionLabel(groupBy, resolved.label)
      : feedEntitySectionLabel(groupBy, resolved.rawKey);
    appendItem(
      sections,
      byKey,
      {
        key: sectionKey,
        label,
        groupBy,
        rawKey: resolved.rawKey,
        declutterable: true,
        items: [],
        eventCount: 0,
      },
      item,
    );
  }

  return sections;
}

/** Parse a persisted hidden-group key back into a display label. */
export function feedHiddenGroupLabel(sectionKey: string): string {
  const [groupBy, ...rest] = sectionKey.split(":");
  const rawKey = rest.join(":");
  if (!groupBy || !rawKey) return sectionKey;
  if (groupBy === "date") {
    const parts = rawKey.split("-").map(Number);
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
      const date = new Date(parts[0], parts[1], parts[2]);
      return feedDayLabel(date.toISOString());
    }
    return rawKey;
  }
  return feedEntitySectionLabel(groupBy as FeedGroupBy, rawKey);
}

export function isFeedHiddenGroupSensitive(sectionKey: string): boolean {
  const groupBy = sectionKey.split(":")[0] as FeedGroupBy;
  return isFeedGroupLabelSensitive(groupBy);
}
